import { useCallback } from "react";
import { useModal } from "../../../context/ModalProvider";
import { useToast } from "../../../context/ToastProvider";
import { userFacingError, ApiError } from "../../../lib/api";
import {
  needsCollisionResolutionFromError,
  type EventRef,
  type JourneyMilestone
} from "../../../lib/journeyApi";
import {
  RoleResolutionSheet,
  type RoleResolutionChoice,
  type RoleResolutionContext
} from "../RoleResolutionSheet";
import { useRoleResolution } from "./useRoleResolution";
import type { EventRoleChange } from "../../JourneyRail/EventsCard";

/**
 * `useEventRoleActions` — the ONE awareness-layer role-change handler (A5/A4,
 * spec §9.4/§10, build-plan §2.2 Gap 4).
 *
 * Every surface that shows a race's role and lets the athlete flip it — the
 * EventsCard chip, the JourneySettings "Tune-ups in this build" promote rows,
 * the BoxTimeline milestone rows, and the calendar tune-up chips — routes its
 * change through here so none reimplements the reclassify / collision path:
 *
 *   - A goal→milestone or milestone→goal change persists via `reclassify`
 *     (S2's `POST /:id/role`), which fires `notifyJourneyChanged` on success.
 *   - A milestone→goal PROMOTE can collide (SF18): the server answers
 *     `409 { needsCollisionResolution, conflicts }`; we open the SAME
 *     `RoleResolutionSheet` (`pair`, built from the 409's conflicts) and resolve
 *     via the shared `applyRoleResolution` (§2.2 — Gap 4).
 *   - SF19 (goal→milestone with no containing build → 422) / SF22 (CSRF, auth)
 *     surface as an honest error toast (SF21 — never a false success).
 */
export type UseEventRoleActionsResult = {
  /** Flip a race's role (goal↔milestone), collision-aware. */
  changeEventRole: (change: EventRoleChange) => Promise<void>;
  /** Promote a nested tune-up back to its own goal race (convenience wrapper). */
  promoteMilestone: (milestone: JourneyMilestone) => void;
};

export function useEventRoleActions(): UseEventRoleActionsResult {
  const { open: openModal, close: closeModal } = useModal();
  const { push: pushToast } = useToast();
  const { reclassify, applyRoleResolution } = useRoleResolution();

  const applyResolution = useCallback(
    async (choice: RoleResolutionChoice, context: RoleResolutionContext): Promise<void> => {
      try {
        await applyRoleResolution(choice, context);
      } catch (err) {
        pushToast({
          tone: "error",
          message: userFacingError(err, "We couldn't sort that out just yet — please try again.")
        });
      }
    },
    [applyRoleResolution, pushToast]
  );

  const openCollisionSheet = useCallback(
    (context: Extract<RoleResolutionContext, { kind: "pair" }>) => {
      const id = openModal(
        <RoleResolutionSheet
          context={context}
          onResolve={(choices) => {
            closeModal(id);
            // Pair sheet → a 1-element batch.
            if (choices[0]) void applyResolution(choices[0], context);
          }}
          onCancel={() => {
            closeModal(id);
            // G4: a cancelled promote-collision would otherwise be silent — the
            // role chip just snaps back. Tell the athlete nothing changed.
            // (Promote-flow wording is fine here: `changeEventRole` is the only
            // caller of `openCollisionSheet` today; revisit if that changes.)
            const name = context.events[0]?.name?.trim() || "your race";
            pushToast({
              tone: "info",
              message: `No changes — ${name} stays a prep race.`
            });
          }}
        />
      );
    },
    [openModal, closeModal, applyResolution, pushToast]
  );

  const changeEventRole = useCallback<UseEventRoleActionsResult["changeEventRole"]>(
    async (change) => {
      try {
        await reclassify(change.eventId, change.to);
      } catch (err) {
        // Promote collision (SF18): open the pair sheet from the 409 conflicts.
        const collision = needsCollisionResolutionFromError(err);
        if (collision) {
          const c = collision.conflicts;
          const newEvent: EventRef = {
            id: change.eventId,
            name: change.name,
            ymd: change.ymd ?? c.newEvent?.ymd ?? "",
            hasJourney: false
          };
          const existingEvent: EventRef = {
            id: c.existing.journeyId,
            name: c.existing.raceName ?? "your current race",
            ymd: c.existing.raceYmd ?? "",
            hasJourney: c.existing.hasJourney ?? true
          };
          const newIsLater =
            !!existingEvent.ymd && !!newEvent.ymd && newEvent.ymd > existingEvent.ymd;
          openCollisionSheet({
            kind: "pair",
            events: [newEvent, existingEvent],
            anchor: newIsLater ? newEvent : existingEvent,
            outcomes: c.resolutions
          });
          return;
        }
        // SF19 / SF22 — no containing build, CSRF, etc: honest error toast.
        if (err instanceof ApiError && err.code === "no_containing_build") {
          const name = change.name.trim() || "This race";
          pushToast({
            tone: "error",
            message:
              change.to === "milestone"
                ? `${name} can't be a prep race — its date isn't inside another race plan's window. Extend or add a later goal that covers that day first.`
                : "We couldn't change that race's role — try again."
          });
          return;
        }
        pushToast({
          tone: "error",
          message: userFacingError(err, "We couldn't change that race's role — try again.")
        });
      }
    },
    [reclassify, openCollisionSheet, pushToast]
  );

  const promoteMilestone = useCallback<UseEventRoleActionsResult["promoteMilestone"]>(
    (milestone) =>
      void changeEventRole({
        eventId: milestone.eventId,
        name: milestone.name,
        ymd: milestone.ymd,
        from: "milestone",
        to: "goal"
      }),
    [changeEventRole]
  );

  return { changeEventRole, promoteMilestone };
}
